<template>
    <div class="max-w-screen-xl px-5 mx-auto" id="form-step-1">
        <div class="mx-auto pt-8 pb-8 sm:pb-16 sm:pt-16">
            <div class="relative font-division-heading text-center">
                <h1 class="text-4xl lg:text-7xl font-bold text-center text-white">
                    Affordable {{ sector }} Lawyer<br>
                    <span v-if="locationRegion" class="block mt-2">{{ locationRegion }}</span>
                </h1>
            </div>

            <div class="mt-5 lg:mt-8 text-center text-white max-w-4xl mx-auto text-lg">
                Enter the location you need legal help below. You'll receive a free consultation at a convenient time, and save up to 80% on upfront legal&nbsp;fees.
            </div>

            <div class="flex flex-col mx-auto mt-8">
                <div v-if="hasRedirectUrl" class="w-full px-4 pt-5 pb-4 mx-auto overflow-hidden text-center bg-white border-2 shadow-xl border-brand-green rounded-xl sm:mt-8 sm:max-w-xl lg:max-w-3xl sm:p-6">
                    <h3 class="text-lg font-medium leading-6 text-white lg:text-2xl" id="modal-title">{{ previousRequestText.notice }}</h3>
                    <div class="mt-2">
                        <p class="text-sm text-neutral-500 lg:text-lg">{{ previousRequestText.body }}</p>
                    </div>
                    <div class="mt-5 sm:mt-4">
                        <CallToActionButton class="w-full text-white" :href="redirectUrl" :text="previousRequestText.button"></CallToActionButton>
                    </div>
                </div>
                <div v-else id="locationsearch">
                    <div v-show="error" class="max-w-4xl mx-auto px-1.5 md:p-0 md:mb-2">
                        <div class="py-3 px-2 text-center text-sm text-red-600 bg-red-100 rounded-xl md:text-base font-division-heading font-light" id="location-search-error-container">
                            <i class="fas fa-exclamation-circle"></i>
                            {{ error }}
                        </div>
                    </div>

                    <div class="max-w-4xl mx-auto md:focus-within:outline md:focus-within:outline-offset-2 md:focus-within:outline-2 md:focus-within:outline-white/50 rounded-xl p-1.5 md:p-0">
                        <div class="md:flex w-full rounded-xl">
                            <location-search-input
                                @error="onError"
                                @input="updateQuery"
                                @location:selected="setLocation"
                                @submit="submit"
                                :city="locationName"
                            ></location-search-input>

                            <button
                                class="w-full py-5 md:py-0 mt-1.5 font-bold uppercase transition-colors rounded-xl md:rounded-l-none md:rounded-r-xl shrink-0 md:mt-0 bg-amber-400 hover:bg-amber-500 focus:bg-amber-500 md:w-auto md:px-6 md:text-lg focus:outline-none focus:ring-2 ring-brand-blue ring-offset-2"
                                type="submit"
                                role="button"
                                :disabled="loading"
                                @keydown.enter="submit"
                                @click="submit"
                            >
                                <span v-show="loading" class="font-bold">
                                    Connecting&hellip; <i class="ml-2 fas fa-spinner fa-pulse fa-fw"></i>
                                </span>

                                <span v-show="! loading" class="block font-division-heading font-bold leading-5 text-neutral-900">
                                    Find a Local<br class="hidden md:block"> Lawyer
                                </span>
                            </button>
                        </div>


                    </div>


                </div>
            </div>

            <h2 class="mt-8 text-center text-white leading-loose lg:leading-none flex flex-col sm:flex-row items-center justify-center">
                <img alt="Testimonial Thumbnails" class="w-48 sm:mr-4" src="/assets/img/lead-capture/testimonials-thumbnails.png"/> <div class="mt-2 sm:mt-0">Over 1 Million People Served Since 2008</div>
            </h2>
        </div>
    </div>
</template>

<script>
import { get, isNull, map, set } from 'lodash';
import { mapGetters } from 'vuex';
import setScrollBarWidth from 'set-scrollbar-width';

import CallToActionButton from '../CallToActionButton';
import LocationSearchInput from '../LocationSearchInput';
import StepLocationBullet from './StepLocationBullet';

export default {
    components: {
        CallToActionButton,
        LocationSearchInput,
        StepLocationBullet,
    },

    data: () => ({
        loading: false,
        error: '',
        query: '',
    }),

    mounted() {
        setScrollBarWidth();

        let errors = {
            invalid_location: 'Please enter a valid location.',
        };

        if (this.errorCode && typeof errors[this.errorCode] !== 'undefined') {
            this.onError(errors[this.errorCode]);
        }

        if (!this.hasRedirectUrl) {
            // BEFORE form_step_mounted, order matters.
            window.dataLayer.push({
                event: 'form_start',
                form_id: 'ulh-lead-intake',
                form_name: 'ULH Lead Intake'
            });

            window.dataLayer.push({
                event: 'form_step_mounted',
                // default only for Step 1, to prevent empty
                form_step_id: window.getCurrentStep() ? window.getCurrentStep() : '1',
                form_step_name: 'location'
            });
        }
    },

    props: {
        previousRequestText: {
            type: Object,
            default() {
                return {};
            },
        },
    },

    computed: {
        ...mapGetters('lead', [
            'city',
            'dynamic',
            'heroBullets',
            'preHeadline',
            'locationId',
            'redirectUrl',
            'state'
        ]),

        /**
         * @returns {boolean}
         */
        hasLocation() {
            return Boolean(get(this, 'city'));
        },

        /**
         * @returns {boolean}
         */
        hasRedirectUrl() {
            return Boolean(this.redirectUrl);
        },

        /**
         * @returns {string|null}
         */
        locationName() {
            if (this.hasLocation) {
                return `${get(this, 'city')}, ${get(this, 'state')}`;
            }

            return null;
        },

        /**
         * @returns {string|null}
         */
        locationRegion() {
            let region = '';

            if (get(window.SHARED, 'location_region')) {
                region += 'in ' + get(window.SHARED, 'location_region');
            }

            if (get(window.SHARED, 'location_state')) {
                region += ', ' + get(window.SHARED, 'location_state');
            }

            return region;
        },

        /**
         * @returns {string}
         */
        sector() {
            const sector = get(window.SHARED, 'sector');

            if (['disorderly conduct', 'extortion', 'manslaughter', 'public intoxication'].includes(sector)) {
                return 'Criminal Defense';
            }

            return sector
                ? (sector === 'dui'
                    ? 'DUI'
                    : this.ucwords(sector))
                : '';
        },

        /**
         * @returns {string}
         */
        sectorWithArticle() {
            return get(window.SHARED, 'sector') === 'dui'
                ? 'a DUI'
                : get(window.SHARED, 'sector_with_article');
        }
    },

    methods: {
        /**
         * Determine if this step can be submitted. Set `loading` and `error` properties as well.
         *
         * @returns {boolean}
         */
        canSubmit() {
            if (this.hasLocation) {
                this.loading = true;

                return true;
            }

            this.error = 'Please enter a location.';
            this.loading = false;

            return false;
        },

        /**
         * Set error from locations search input.
         *
         * @param {string} error
         */
        onError(error) {
            this.error = error;
        },

        /**
         * Record necessary location information in local storage.
         */
        recordInLocalStorage() {
            let form = window.localStorage.getItem('lcsForm');

            form = isNull(form)
                ? {}
                : JSON.parse(form);

            set(form, 'city', this.city);
            set(form, 'locationId', this.locationId);
            set(form, 'state', this.state);

            window.localStorage.setItem('lcsForm', JSON.stringify(form));
        },

        /**
         * Set location in vuex store, reset error, then submit step.
         *
         * @param {object} location
         */
        setLocation(location) {
            this.$store.commit('lead/location', location)

            this.error = '';
        },

        /**
         * Submit this step.
         */
        submit() {
            if (this.canSubmit()) {
                this.recordInLocalStorage();
                this.$store.commit('lead/city', this.city);

                window.dataLayer.push({
                    event: 'form_step_submit',
                    // default only for Step 1, to prevent empty
                    form_step_id: window.getCurrentStep() ? window.getCurrentStep() : '1',
                    form_step_name: 'location'
                });

                this.$event.$emit('step:next');
            }
        },

        /**
         * Javascript version of php's ucwords: Capitalize the first letter of each word in given string.
         *
         * @param {string} string
         *
         * @returns {string}
         */
        ucwords(string) {
            let words = string.split(' ');

            return map(words, (word) => word[0].toUpperCase() + word.substring(1))
                .join(' ');
        },

        /**
         * Update query... duh. Why? No idea.
         *
         * @param {string} event
         */
        updateQuery(event) {
            this.query = event;
        },

        /**
         * Smooth scroll to the selected element.
         *
         * @param {string} event
         */
        scrollTo(selector) {
            document.querySelector(selector).scrollIntoView({ behavior: 'smooth' });
        }
    }
}
</script>
